/** "Possible duplicate" banner for the lead drawer (Tom 2026-08-13: "if there
 * is a duplicate I want the system to let me know there is a duplicate and which
 * is the other one ... and maybe the ability to merge it").
 *
 * Self-contained: fetches its own twins, renders nothing when there are none, and
 * a failed load never blocks the drawer. Merge keeps the lead Tom is viewing and
 * folds the other into it (recoverable — the other is tombstoned, not deleted). */
import { useEffect, useState } from "react";
import { fetchDuplicates, mergeLeads, type DuplicateLead } from "./dedupeApi";

export function DuplicateBanner({ address, token }: { address: string; token: string | null }) {
  const [dupes, setDupes] = useState<DuplicateLead[]>([]);
  const [confirming, setConfirming] = useState<string | null>(null); // address awaiting confirm
  const [busy, setBusy] = useState<string | null>(null);             // address being merged
  const [err, setErr] = useState<string>("");
  const [mergedCount, setMergedCount] = useState(0);

  useEffect(() => {
    let live = true;
    setDupes([]); setConfirming(null); setBusy(null); setErr(""); setMergedCount(0);
    fetchDuplicates(token, address).then((rows) => { if (live) setDupes(rows); });
    return () => { live = false; };
  }, [address, token]);

  if (dupes.length === 0) return null;

  async function doMerge(other: string) {
    setBusy(other); setErr("");
    try {
      await mergeLeads(token, address, other);        // keep = this lead, drop = other
      setDupes((d) => d.filter((x) => x.address !== other));
      setMergedCount((n) => n + 1);
      setConfirming(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Merge failed — try again.");
    } finally {
      setBusy(null);
    }
  }

  const wrap: React.CSSProperties = {
    margin: "10px 14px 0", padding: "10px 12px", borderRadius: 10,
    background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.45)",
    color: "var(--text, #1f2937)", fontSize: 13, lineHeight: 1.4,
  };
  const row: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
    padding: "6px 0", borderTop: "1px solid rgba(245,158,11,0.25)",
  };
  const btn: React.CSSProperties = {
    fontSize: 12, padding: "4px 10px", borderRadius: 7, cursor: "pointer",
    border: "1px solid rgba(0,0,0,0.15)", background: "#fff", color: "#92400e", fontWeight: 600,
  };
  const btnGo: React.CSSProperties = { ...btn, background: "#b45309", color: "#fff", border: "1px solid #b45309" };

  return (
    <div style={wrap}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>
        ⚠️ Possible duplicate{dupes.length > 1 ? "s" : ""} — this house looks like it&apos;s already a lead
      </div>
      <div style={{ opacity: 0.85, marginBottom: 4 }}>
        Merging keeps <b>this</b> lead and folds the other one in (its texts, tasks and deals follow;
        it&apos;s tombstoned, not deleted).
      </div>
      {dupes.map((d) => (
        <div key={d.address} style={row}>
          <span style={{ flex: "1 1 220px", minWidth: 180 }}>
            <b>{d.address}</b>
            {d.source ? <span style={{ opacity: 0.7 }}> · {d.source}</span> : null}
            <span style={{ opacity: 0.7 }}> · matched on {d.match === "parcel" ? "parcel #" : "address"}</span>
          </span>
          {confirming === d.address ? (
            <>
              <span style={{ color: "#92400e" }}>Keep this address, remove the other?</span>
              <button style={btnGo} disabled={busy === d.address}
                onClick={() => doMerge(d.address)}>{busy === d.address ? "Merging…" : "Confirm merge"}</button>
              <button style={btn} disabled={busy === d.address}
                onClick={() => setConfirming(null)}>Cancel</button>
            </>
          ) : (
            <button style={btn} onClick={() => { setConfirming(d.address); setErr(""); }}>Merge into this</button>
          )}
        </div>
      ))}
      {err ? <div style={{ color: "#b91c1c", marginTop: 6 }}>{err}</div> : null}
      {mergedCount > 0 ? (
        <div style={{ color: "#166534", marginTop: 6 }}>
          Merged ✓ — reopen the lead to see the combined details.
        </div>
      ) : null}
    </div>
  );
}
